/**
 * RelatedProductGroupEditor — Edit, for a Related Products card.
 *
 * Edit used to open the per-item Vendor Options modal for `group.item`. That
 * modal is a good editor for ONE purchasing option's package, SKU, price and
 * status, but it is not an editor for the thing on screen: a Related Products
 * card is a whole EQUIVALENT GROUP — several inventory items joined by
 * confirmed same_item rows, each contributing its vendor products — and the
 * card's own canonical item is only one member of it. Clicking Edit on a card
 * showing three vendors and being given one item's options is the bug this
 * component exists to fix.
 *
 * What it edits is MEMBERSHIP. Two underlying shapes, two existing
 * non-destructive operations, and no third one invented here:
 *
 *   CASE A — the row's vendor product is the only thing its member item
 *            contributes. Removing it means the ITEM leaves the group: the
 *            confirmed same_item rows tying it to the rest are rejected
 *            (rejectRelationship). The item stays active, keeps its purchase
 *            options, is never archived and is never deleted.
 *
 *   CASE B — the row's member item contributes SEVERAL vendor products (one
 *            inventory item carrying several purchase_options). Removing one
 *            of them means splitting that option onto its own inventory item
 *            (detachOptionToNewItem → detach_purchase_option_to_new_item),
 *            which is the audited path the relationship workspace already
 *            uses. The vendor product survives as a standalone item.
 *
 * combineItems is deliberately NOT used: it archives one side as part of a
 * merge, and the architecture here is link-don't-merge.
 *
 * Adding a member creates a CONFIRMED same_item relationship
 * (createManualRelationship) — again a link, never a merge, and every one of
 * the added item's active vendor products then joins the card through the
 * existing folded projection.
 *
 * Prices are never computed here. Every membership change, and the manual
 * Recalculate group button, call the SAME server pass that built the group's
 * comparison in the first place (requestGroupComparison → items-ai-match
 * 'group_comparison' → equivalent_group_comparisons). This component shows
 * the numbers the card already computed from that stored record, waits while
 * the server recomputes, and re-reads.
 */
import React, { useMemo, useState } from 'react';
import { createManualRelationship, rejectRelationship } from '../services/itemRelationshipService';
import { detachOptionToNewItem } from '../services/itemCrossReferenceService';
import { requestGroupComparison } from '../services/comparisonRecomputeService';
import { RelationshipItemPicker } from './ManageRelationshipsView';

const text = (value) => String(value ?? '').trim();

const itemVendor = (item, optionsByItemId, vendorNameById) => {
  const opts = (optionsByItemId?.[item?.id] || []).filter((o) => o?.status !== 'inactive');
  const preferred = opts.find((o) => o.is_preferred) || opts[0] || null;
  if (preferred) {
    const v = String(vendorNameById?.[preferred.vendor_id] || preferred.vendor_name_text || item?.vendor || '').trim();
    if (v) return v;
  }
  return String(item?.vendor || '').trim() || 'Vendor not set';
};

const money = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? `$${parsed.toFixed(2)}` : null;
};

const styles = {
  head: { display: 'grid', gap: 3 },
  title: { color: '#3E2723', fontSize: 17, fontWeight: 800, margin: 0 },
  meta: { color: '#8B7B73', fontSize: 11.5 },
  sectionLabel: {
    color: '#6B5A52', fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '.05em', margin: '0 0 6px',
  },
  rows: { display: 'grid', gap: 8 },
  row: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10,
    border: '1px solid #EEE5DF', borderRadius: 10, background: '#FBF8F5', padding: '9px 10px',
  },
  rowWinner: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10,
    border: '1px solid #C7DCCB', borderRadius: 10, background: '#F3F8F3', padding: '9px 10px',
  },
  rowBody: { minWidth: 0 },
  vendor: { color: '#3E2723', fontSize: 13, fontWeight: 750, lineHeight: 1.25 },
  product: { color: '#7D6D64', fontSize: 11, marginTop: 2, lineHeight: 1.35 },
  price: { color: '#5A4A42', fontSize: 11.5, marginTop: 4 },
  priceLabel: {
    color: '#8B7B73', fontSize: 9, fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '.04em', marginRight: 4,
  },
  needsConfirm: { color: '#8A5A00', fontSize: 11, marginTop: 4, lineHeight: 1.35 },
  needsConfirmButton: {
    display: 'block', width: '100%', border: 0, padding: 0, background: 'transparent',
    textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: 2,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  badge: {
    display: 'inline-flex', marginLeft: 5, padding: '1px 6px', borderRadius: 999,
    background: '#DCEBE0', color: '#22593E', fontSize: 8.5, fontWeight: 800,
    textTransform: 'uppercase', letterSpacing: '.03em',
  },
  quietBadge: {
    display: 'inline-flex', marginLeft: 5, padding: '1px 5px', borderRadius: 999,
    border: '1px solid #E0D6CC', background: '#FBF8F5', color: '#7D6D64',
    fontSize: 8.5, fontWeight: 800, textTransform: 'uppercase',
  },
  rowActions: { display: 'grid', gap: 4, justifyItems: 'end', flexShrink: 0 },
  confirm: {
    border: '1px solid #E7D9BE', borderRadius: 9, background: '#FFFBF0', padding: '8px 10px',
    color: '#6F5A22', fontSize: 11.5, lineHeight: 1.45, marginTop: 6,
  },
  confirmActions: { display: 'flex', gap: 6, marginTop: 7 },
  addBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  summary: {
    border: '1px solid #E8DDD4', borderRadius: 11, background: '#FAF7F4', padding: '10px 12px',
    color: '#5A4A42', fontSize: 12, display: 'grid', gap: 3,
  },
  divider: { borderTop: '1px solid #EEE5DF', margin: '14px 0' },
  status: { color: '#6B5A52', fontSize: 11.5 },
  working: { color: '#8A5A00', fontSize: 11.5, fontWeight: 700 },
  error: {
    background: '#FDECEA', border: '1px solid #F5C2BD', color: '#B71C1C',
    borderRadius: 8, padding: '8px 10px', fontSize: 12,
  },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
    flexWrap: 'wrap', marginTop: 14,
  },
};

// Plain-English for every machine reason a vendor row can carry instead of a
// comparable price. Mirrors RelatedProductGroupsView's list on purpose: the
// editor and the card must never explain the same state two different ways.
const UNRESOLVED_REASONS = {
  no_stored_comparison: 'this pack has not been measured against the group’s unit yet.',
  no_stored_quantity: 'the pack size behind this price is not known yet.',
  unit_not_convertible: 'this pack is measured in a different dimension from the rest of the group.',
  package_basis_conflict: 'this pack’s recorded facts disagree with each other.',
  fingerprint_mismatch: 'the pack changed since it was last measured.',
  same_unit_quantity_disagreement: 'the stored quantity disagrees with the pack on file.',
  converted_quantity_disagreement: 'the stored quantity disagrees with the pack on file.',
  no_row: 'this vendor product was not measured in this comparison.',
};

/**
 * The confirmed same_item rows tying one member to the REST of this group.
 * Rejecting exactly these takes the member out of the equivalent working set
 * and leaves every other edge — and every catalog row — alone.
 */
export function equivalentEdgesWithinGroup(relationships, memberItemId, memberItemIds) {
  const memberKey = String(memberItemId);
  const inGroup = new Set((memberItemIds || []).map(String));
  return (Array.isArray(relationships) ? relationships : []).filter((row) => {
    if (!row || row.relationship_type !== 'same_item' || row.status !== 'confirmed') return false;
    const a = String(row.item_a_id);
    const b = String(row.item_b_id);
    if (a === memberKey) return inGroup.has(b) && b !== memberKey;
    if (b === memberKey) return inGroup.has(a) && a !== memberKey;
    return false;
  });
}

/**
 * Which non-destructive operation a Remove means for one visible row.
 *
 * 'detach'  — this member item contributes more than one vendor product, so
 *             the option is split onto its own inventory item.
 * 'unlink'  — this member contributes only this product, so the ITEM leaves
 *             the group and its confirmed same_item edges are rejected.
 * null      — nothing safe to do (a legacy row with no option id on a
 *             single-member group); the button is not offered.
 */
export function removalPlanFor(product, group) {
  if (!product || !group) return null;
  const memberItemId = product.memberItemId ?? group.itemId;
  const products = group.products || [];
  const siblingCount = products.filter((entry) => (
    String(entry.memberItemId ?? group.itemId) === String(memberItemId)
  )).length;
  // `source: 'legacy'` products are an item's own vendor/price columns
  // standing in for a purchase option it does not have. Their id is a
  // synthetic 'legacy-<itemId>', so they can never be detached — only their
  // ITEM can leave the group.
  const isOption = product.id != null && product.source !== 'legacy';
  if (isOption && siblingCount > 1) {
    return { kind: 'detach', memberItemId, optionId: product.id };
  }
  const memberIds = (group.memberItemIds || []).map(String);
  if (memberIds.length > 1) return { kind: 'unlink', memberItemId, optionId: isOption ? product.id : null };
  if (isOption) return { kind: 'detach', memberItemId, optionId: product.id };
  return null;
}

export default function RelatedProductGroupEditor({
  group,
  factsByOptionId = null,
  comparisonUnitLabel = '',
  itemRelationships = [],
  items = [],
  optionsByItemId = {},
  vendorNameById = {},
  itemById = new Map(),
  storeId,
  onChanged,
  onEditItem,
  onClose,
}) {
  const [busyKey, setBusyKey] = useState('');
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [confirmDetach, setConfirmDetach] = useState(null);
  const [confirmUnlink, setConfirmUnlink] = useState(null);
  const [confirmAdd, setConfirmAdd] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addQuery, setAddQuery] = useState('');

  const memberItemIds = useMemo(
    () => (group?.memberItemIds?.length ? group.memberItemIds : [group?.itemId]).filter((id) => id != null),
    [group],
  );
  const excludeIds = useMemo(() => memberItemIds.map(String), [memberItemIds]);

  const lowest = useMemo(() => {
    if (!factsByOptionId) return null;
    const winner = (group?.products || []).find(
      (product) => factsByOptionId.get(String(product.id))?.isLowest === true,
    );
    return winner || null;
  }, [factsByOptionId, group]);
  const savingsLine = lowest ? factsByOptionId?.get(String(lowest.id))?.savingsLine : null;

  const busy = busyKey !== '';

  /**
   * One shape for every membership change: persist it, then run the SAME
   * server group-comparison pass that built the group, then re-read. The
   * membership write is never rolled back by a failed recompute — the group's
   * stored comparison simply stays as it was and says so.
   */
  const runChange = async (key, mutate, anchorIds) => {
    setBusyKey(key);
    setError('');
    setWarning('');
    setPhase('saving');
    try {
      await mutate();
    } catch (mutationError) {
      setError(mutationError?.message || 'That change could not be saved. Nothing was altered.');
      setPhase('idle');
      setBusyKey('');
      return false;
    }
    setPhase('recalculating');
    let recomputed = null;
    try {
      recomputed = await requestGroupComparison(storeId, anchorIds);
    } catch (recomputeError) {
      recomputed = null;
    }
    if (!recomputed) {
      setWarning('The change was saved, but prices could not be recalculated just now. Use Recalculate group to try again.');
    }
    try {
      if (typeof onChanged === 'function') await onChanged();
    } catch (refreshError) {
      setWarning('The change was saved. Refresh the catalog to see it.');
    }
    setPhase('updated');
    setBusyKey('');
    return true;
  };

  const removeProduct = (product) => {
    const plan = removalPlanFor(product, group);
    if (!plan) return;
    if (plan.kind === 'detach') {
      // Splitting a vendor product onto its own inventory item is the one
      // remove that changes the catalog's shape, so it is the one remove that
      // asks first. It still deletes nothing.
      setConfirmDetach({ product, plan });
      return;
    }
    // Unlinking a member item needs explicit confirmation — best practice for
    // a destructive group-membership change.
    setConfirmUnlink({ product, plan });
  };

  const confirmUnlinkNow = () => {
    if (!confirmUnlink) return;
    const { product, plan } = confirmUnlink;
    setConfirmUnlink(null);
    void runChange(
      `remove:${product.id ?? plan.memberItemId}`,
      async () => {
        const edges = equivalentEdgesWithinGroup(itemRelationships, plan.memberItemId, memberItemIds);
        for (const row of edges) {
          // eslint-disable-next-line no-await-in-loop
          await rejectRelationship(row.id);
        }
      },
      memberItemIds,
    );
  };

  const confirmDetachNow = () => {
    if (!confirmDetach) return;
    const { product, plan } = confirmDetach;
    setConfirmDetach(null);
    void runChange(
      `remove:${product.id}`,
      async () => { await detachOptionToNewItem(plan.optionId); },
      memberItemIds,
    );
  };

  const confirmAddNow = () => {
    if (!confirmAdd) return;
    const item = confirmAdd;
    setConfirmAdd(null);
    setAddOpen(false);
    setAddQuery('');
    const anchorId = group?.canonicalItemId ?? group?.itemId;
    void runChange(
      `add:${item.id}`,
      async () => { await createManualRelationship(storeId, anchorId, item.id, 'same_item'); },
      [...memberItemIds, item.id],
    );
  };

  const addItem = (item) => {
    // Adding to a group influences purchase choices — confirm explicitly.
    setConfirmAdd(item);
  };

  const recalculate = async () => {
    setBusyKey('recalculate');
    setError('');
    setWarning('');
    setPhase('recalculating');
    const result = await requestGroupComparison(storeId, memberItemIds);
    if (!result) {
      setError('The group could not be recalculated just now. Nothing was changed.');
      setPhase('idle');
      setBusyKey('');
      return;
    }
    try {
      if (typeof onChanged === 'function') await onChanged();
    } catch (refreshError) {
      setWarning('The group was recalculated. Refresh the catalog to see it.');
    }
    setPhase('updated');
    setBusyKey('');
  };

  if (!group) return null;
  const products = group.products || [];

  return (
    <div data-testid="related-product-group-editor">
      <div style={styles.head}>
        <h4 style={styles.title}>{group.canonicalName}</h4>
        <div style={styles.meta} data-testid="group-editor-meta">
          {products.length} way{products.length === 1 ? '' : 's'} to buy
          {memberItemIds.length > 1 ? ` · ${memberItemIds.length} linked items` : ''}
          {' · counted in '}{group.countUnit}
          {group.category ? ` · ${group.category}` : ''}
        </div>
      </div>

      {error && <div style={{ ...styles.error, marginTop: 10 }} role="alert" data-testid="group-editor-error">{error}</div>}
      {warning && !error && (
        <div style={{ ...styles.confirm, marginTop: 10 }} role="status" data-testid="group-editor-warning">{warning}</div>
      )}

      <div style={styles.divider} />

      <p style={styles.sectionLabel}>Current group</p>
      <div style={styles.rows}>
        {products.map((product) => {
          const facts = factsByOptionId?.get(String(product.id)) || null;
          const plan = removalPlanFor(product, group);
          const memberItemId = product.memberItemId ?? group.itemId;
          const memberItem = itemById.get?.(String(memberItemId)) || null;
          const winner = facts?.isLowest === true;
          return (
            <div
              key={product.id || `${memberItemId}-${product.vendor}`}
              style={winner ? styles.rowWinner : styles.row}
              data-testid="group-editor-product"
              data-option-id={product.id ?? undefined}
              data-member-item-id={String(memberItemId)}
            >
              <div style={styles.rowBody}>
                <div style={styles.vendor}>
                  {text(product.vendor) || 'Vendor not set'}
                  {facts?.isLowest && (
                    <span style={styles.badge} data-testid="group-editor-lowest">
                      {facts.isTied ? 'Tied lowest' : 'Lowest price'}
                    </span>
                  )}
                  {product.isCanonical && (
                    <span
                      style={styles.quietBadge}
                      title="The item that represents this group in the catalog, count sessions and pars"
                    >
                      Count item
                    </span>
                  )}
                  {facts?.isLastUsed && (
                    <span style={styles.quietBadge} title={facts.lastUsedTitle || 'The way this was bought most recently'}>
                      Last used
                    </span>
                  )}
                  {product.isPreferred && <span style={styles.quietBadge}>Preferred</span>}
                </div>
                {text(product.name) && text(product.name) !== group.canonicalName && (
                  <div style={styles.product}>{product.name}</div>
                )}
                {text(product.memberName) && text(product.memberName) !== group.canonicalName && (
                  <div style={styles.product} data-testid="group-editor-member-name">
                    Catalog item: {product.memberName}
                  </div>
                )}
                <div style={styles.price} data-testid="group-editor-purchase">
                  <span style={styles.priceLabel}>Purchase</span>
                  {[text(product.pack) || null, money(product.packagePrice)]
                    .filter(Boolean).join(' — ') || 'No price on file'}
                </div>
                {facts?.comparable ? (
                  <div style={styles.price} data-testid="group-editor-comparable">
                    <span style={styles.priceLabel}>Comparable</span>
                    {facts.estimated ? `~${facts.comparable}` : facts.comparable}
                  </div>
                ) : typeof onEditItem === 'function' && memberItem ? (
                  <button
                    type="button"
                    style={{ ...styles.needsConfirm, ...styles.needsConfirmButton }}
                    data-testid="group-editor-needs-confirmation"
                    onClick={() => onEditItem(memberItem, {
                      reviewComparison: true,
                      comparisonUnit: text(comparisonUnitLabel),
                    })}
                    aria-label={`Review comparison quantity for ${text(product.vendor) || product.name || group.canonicalName}`}
                  >
                    Needs quantity confirmation — {UNRESOLVED_REASONS[facts?.unresolvedReason]
                      || UNRESOLVED_REASONS.no_stored_comparison}
                  </button>
                ) : (
                  <div style={styles.needsConfirm} data-testid="group-editor-needs-confirmation">
                    Needs quantity confirmation — {UNRESOLVED_REASONS[facts?.unresolvedReason]
                      || UNRESOLVED_REASONS.no_stored_comparison}
                  </div>
                )}
              </div>
              <div style={styles.rowActions}>
                {plan && (
                  <button
                    type="button"
                    className="btn btn-sm p-0"
                    disabled={busy}
                    data-testid="group-editor-remove"
                    aria-label={`Remove ${text(product.vendor) || 'this vendor product'} from ${group.canonicalName}`}
                    onClick={() => removeProduct(product)}
                    title={`Remove ${text(product.vendor) || 'this vendor product'} from ${group.canonicalName}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      border: '1.5px solid #E6AAA8', borderRadius: 999, background: '#fff',
                      color: '#A32018', padding: '2px 9px', fontSize: 11, fontWeight: 750,
                      lineHeight: 1, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    <span aria-hidden="true" style={{ fontSize: 12, lineHeight: 1 }}>×</span> Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {confirmDetach && (
        <div style={styles.confirm} role="alertdialog" data-testid="group-editor-detach-confirm">
          <strong>{text(confirmDetach.product.vendor) || 'This vendor product'}</strong> is one of several vendor
          products on the same catalog item, so removing it from this group splits it onto its own catalog
          item. The vendor product, its package and its price are kept — nothing is deleted.
          <div style={styles.confirmActions}>
            <button type="button" className="btn btn-sm btn-primary" data-testid="group-editor-detach-confirm-yes" onClick={confirmDetachNow}>
              Split it out
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmDetach(null)}>
              Keep it here
            </button>
          </div>
        </div>
      )}

      {confirmUnlink && (
        <div style={styles.confirm} role="alertdialog" data-testid="group-editor-unlink-confirm">
          <div>Are you sure you wish to remove <strong>{text(confirmUnlink.product.name) || text(confirmUnlink.product.vendor) || 'this item'}</strong> from <strong>{text(confirmUnlink.product.vendor) || 'this vendor'}</strong> from <strong>{group.canonicalName}</strong>?</div>
          <div style={styles.confirmActions}>
            <button type="button" className="btn btn-sm btn-danger" data-testid="group-editor-unlink-confirm-yes" onClick={confirmUnlinkNow}>
              Yes, remove
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" data-testid="group-editor-unlink-confirm-no" onClick={() => setConfirmUnlink(null)}>
              No, keep it
            </button>
          </div>
        </div>
      )}

      <div style={styles.divider} />

      <div style={styles.addBar}>
        <div>
          <p style={{ ...styles.sectionLabel, margin: 0 }}>Add another item</p>
          <div style={styles.meta}>
            Search your catalog for another way to buy this product. It is linked, never merged — the item
            keeps its own catalog row and every one of its vendor products joins this group.
          </div>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={busy}
          data-testid="group-editor-add-toggle"
          onClick={() => setAddOpen((open) => !open)}
        >
          {addOpen ? 'Close search' : '+ Add another item'}
        </button>
      </div>
      {confirmAdd && (
        <div style={styles.confirm} role="alertdialog" data-testid="group-editor-add-confirm">
          <div>Confirm, add <strong>{text(confirmAdd.description) || `Item ${confirmAdd.id}`}</strong> from <strong>{itemVendor(confirmAdd, optionsByItemId, vendorNameById)}</strong> to <strong>{group.canonicalName}</strong>?</div>
          <div style={{ color: '#6F5A22', fontSize: 11, marginTop: 4, lineHeight: 1.45 }}>Note this may direct purchase choices from this group to this item.</div>
          <div style={styles.confirmActions}>
            <button type="button" className="btn btn-sm btn-primary" data-testid="group-editor-add-confirm-yes" onClick={confirmAddNow}>
              Confirm, add
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" data-testid="group-editor-add-confirm-no" onClick={() => setConfirmAdd(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {addOpen && (
        <RelationshipItemPicker
          idPrefix="group-editor-add"
          items={items}
          optionsByItemId={optionsByItemId}
          vendorNameById={vendorNameById}
          query={addQuery}
          onQueryChange={setAddQuery}
          selectedId=""
          excludeIds={excludeIds}
          onPick={addItem}
        />
      )}

      <div style={styles.divider} />

      <div style={styles.summary} data-testid="group-editor-summary">
        <div>
          <strong>Comparison unit:</strong>{' '}
          {text(comparisonUnitLabel) || 'not settled yet'}
        </div>
        <div>
          <strong>Lowest price:</strong>{' '}
          {lowest ? (text(lowest.vendor) || 'Vendor not set') : 'no ranked winner yet'}
        </div>
        <div>
          <strong>Savings:</strong> {savingsLine || 'not quotable while a priced vendor is uncompared'}
        </div>
      </div>

      <div style={styles.footer}>
        <div>
          {phase === 'saving' && <span style={styles.working} data-testid="group-editor-phase">Saving…</span>}
          {phase === 'recalculating' && (
            <span style={styles.working} data-testid="group-editor-phase">Recalculating prices…</span>
          )}
          {phase === 'updated' && !busy && (
            <span style={styles.status} data-testid="group-editor-phase">Updated just now</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={busy}
            data-testid="group-editor-recalculate"
            onClick={recalculate}
          >
            {busyKey === 'recalculate' ? 'Recalculating…' : 'Recalculate group'}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={busy}
            data-testid="group-editor-done"
            onClick={() => (typeof onClose === 'function' ? onClose() : undefined)}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
